import Fuse from "fuse.js";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MachineContext } from "../context/MachineContext";
import { SiteContentContext } from "../context/SiteContentContext.jsx";
import { useLocalePath } from "../hooks/useLocalePath";
import { useScrollLock } from "../hooks/useScrollLock";
import { machineCategoryUrlSlug, slugify } from "../slug.js";
import ContentText from "./contentText.jsx";

const FUSE_OPTIONS = {
  keys: ["title", "category", "manufacturer"],
  threshold: 0.35,
};

const MAX_RESULTS = 12;

export default function SearchModal({ onClose }) {
  const { machines } = useContext(MachineContext);
  const { getContent } = useContext(SiteContentContext);
  const { localePath } = useLocalePath();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const closeButtonRef = useRef(null);
  const resultsRef = useRef(null);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const fuse = useMemo(() => new Fuse(machines, FUSE_OPTIONS), [machines]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    return fuse.search(query).slice(0, MAX_RESULTS);
  }, [fuse, query]);

  // Autofocus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Lock scroll
  useScrollLock(true);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  // Reset active index when results change
  // biome-ignore lint/correctness/useExhaustiveDependencies: setActiveIndex is a stable setter
  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  // Scroll active result into view
  useEffect(() => {
    if (activeIndex < 0 || !resultsRef.current) return;
    const el = resultsRef.current.children[activeIndex];
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function selectMachine(machine) {
    navigate(localePath(`/machines/${machine.id}`));
    onClose();
  }

  function handleKeyDown(e) {
    if (e.key === "Tab" && e.currentTarget === inputRef.current) {
      e.preventDefault();
      closeButtonRef.current?.focus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0 && results[activeIndex]) {
      e.preventDefault();
      selectMachine(results[activeIndex].item);
    }
  }

  function handleCloseKeyDown(e) {
    if (e.key === "Tab") {
      e.preventDefault();
      inputRef.current?.focus();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal card */}
      <div
        className="relative w-full max-w-2xl bg-surface rounded-xl shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label={getContent("search.input_label", "Sök maskiner")}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-subtle">
          <svg
            className="w-5 h-5 text-text-muted flex-shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <title>Sök</title>
            <circle cx="11" cy="11" r="8" />
            <path strokeLinecap="round" d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={getContent("search.placeholder", "Sök maskiner...")}
            className="flex-1 bg-transparent text-lg text-text-body placeholder:text-text-muted outline-none"
            aria-label={getContent("search.input_label", "Sök maskiner")}
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 text-xs text-text-muted bg-surface-muted px-2 py-1 rounded border border-border-subtle">
            ESC
          </kbd>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            onKeyDown={handleCloseKeyDown}
            className="text-sm text-text-muted hover:text-text-body"
            aria-label={getContent("search.close_label", "Stäng sök")}
          >
            ×
          </button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div ref={resultsRef} className="max-h-[60vh] overflow-y-auto py-2" role="listbox">
            {results.map((result, index) => (
              <div
                key={result.item.id}
                className={`w-full px-5 py-3 flex items-center gap-4 transition-colors ${
                  index === activeIndex
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-surface-soft text-text-body"
                }`}
              >
                {/* The machine itself stays a button so arrow keys, Enter and the click
                    behaviour are untouched; only the pills became links. */}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  onClick={() => selectMachine(result.item)}
                  className="flex items-center gap-4 min-w-0 flex-1 text-left bg-transparent"
                >
                  {result.item.images?.[0] ? (
                    <img
                      src={result.item.images[0]}
                      alt=""
                      className="w-12 h-12 object-cover rounded flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-surface-muted rounded flex-shrink-0 flex items-center justify-center text-text-muted text-xs">
                      —
                    </div>
                  )}
                  <span className="font-medium truncate">{result.item.title}</span>
                </button>

                <div className="flex flex-wrap justify-end gap-2 flex-shrink-0">
                  {result.item.manufacturer && (
                    <Link
                      to={localePath(`/manufacturers/${slugify(result.item.manufacturer)}`)}
                      onClick={onClose}
                      className="text-xs text-text-muted bg-surface-muted px-2 py-0.5 rounded-full hover:text-primary"
                    >
                      {result.item.manufacturer}
                    </Link>
                  )}
                  {/* Only the slug names a real page — the display name would build a URL that
                      404s, so a machine without one keeps a plain pill, like the machine page. */}
                  {result.item.category &&
                    (result.item.categorySlug ? (
                      <Link
                        to={localePath(`/catalogue/${machineCategoryUrlSlug(result.item)}`)}
                        onClick={onClose}
                        className="text-xs text-text-muted bg-surface-muted px-2 py-0.5 rounded-full hover:text-primary"
                      >
                        {result.item.category}
                      </Link>
                    ) : (
                      <span className="text-xs text-text-muted bg-surface-muted px-2 py-0.5 rounded-full">
                        {result.item.category}
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {query.trim() && results.length === 0 && (
          <div className="px-5 py-8 text-center text-text-muted text-sm">
            <ContentText contentKey="search.no_results" fallback="Inga maskiner matchar" /> &ldquo;
            {query}&rdquo;
          </div>
        )}

        {/* Hint when empty */}
        {!query.trim() && (
          <div className="px-5 py-8 text-center text-text-muted text-sm">
            <ContentText
              contentKey="search.hint"
              fallback="Börja skriva för att söka bland {count} maskiner"
              replace={{ "{count}": machines.length }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
